/**
 * Product Track Sheet Template Form
 * Product/Project tracking and status
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const ProductTrackSheetForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: '', product_owner: '', date: new Date().toISOString().split('T')[0], sprint: '',
    features: [{ id: '', feature_name: '', description: '', priority: 'Medium', status: 'Backlog', assignee: '', estimated_hours: 0, actual_hours: 0, start_date: '', due_date: '', completion_date: '', progress: 0, blockers: '', notes: '' }],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    project_name: 'E-Commerce Platform v2.0',
    product_owner: 'Sarah Johnson',
    date: new Date().toISOString().split('T')[0],
    sprint: 'Sprint 5',
    features: [
      { id: 'FEAT-101', feature_name: 'User Authentication Revamp', description: 'Implement OAuth2 with Google and Apple sign-in', priority: 'Critical', status: 'In Progress', assignee: 'John Smith', estimated_hours: 40, actual_hours: 32, start_date: '2025-01-06', due_date: '2025-01-17', completion_date: '', progress: 80, blockers: '', notes: 'Waiting for Apple developer account approval' },
      { id: 'FEAT-102', feature_name: 'Shopping Cart Optimization', description: 'Improve cart performance and add wishlist sync', priority: 'High', status: 'In Progress', assignee: 'Jane Doe', estimated_hours: 24, actual_hours: 18, start_date: '2025-01-08', due_date: '2025-01-17', completion_date: '', progress: 75, blockers: '', notes: '' },
      { id: 'FEAT-103', feature_name: 'Payment Gateway Integration', description: 'Add Stripe and PayPal support', priority: 'Critical', status: 'Ready', assignee: 'Mike Wilson', estimated_hours: 32, actual_hours: 0, start_date: '', due_date: '2025-01-24', completion_date: '', progress: 0, blockers: 'Waiting for Stripe API keys', notes: '' },
      { id: 'FEAT-104', feature_name: 'Order Tracking Dashboard', description: 'Real-time order status and delivery tracking', priority: 'High', status: 'Backlog', assignee: '', estimated_hours: 28, actual_hours: 0, start_date: '', due_date: '2025-01-31', completion_date: '', progress: 0, blockers: '', notes: 'Depends on FEAT-103' },
      { id: 'FEAT-105', feature_name: 'Product Reviews System', description: 'Allow customers to rate and review products', priority: 'Medium', status: 'Completed', assignee: 'Alex Kumar', estimated_hours: 20, actual_hours: 22, start_date: '2025-01-02', due_date: '2025-01-10', completion_date: '2025-01-09', progress: 100, blockers: '', notes: 'Completed ahead of schedule' },
      { id: 'FEAT-106', feature_name: 'Admin Inventory Management', description: 'Bulk import/export and stock alerts', priority: 'Medium', status: 'In Progress', assignee: 'Lisa Chen', estimated_hours: 36, actual_hours: 12, start_date: '2025-01-13', due_date: '2025-01-24', completion_date: '', progress: 35, blockers: '', notes: '' },
      { id: 'FEAT-107', feature_name: 'Mobile Responsive Redesign', description: 'Optimize all pages for mobile devices', priority: 'High', status: 'Ready', assignee: 'David Park', estimated_hours: 40, actual_hours: 0, start_date: '', due_date: '2025-01-31', completion_date: '', progress: 0, blockers: '', notes: 'Design mockups approved' }
    ],
    rai_assignments: [
      { task: 'Review sprint progress', responsible: 'Scrum Master', accountable: 'Product Owner', informed: 'Team', due_date: '2025-01-17', status: 'Pending' }
    ],
    followups: [
      { item: 'Obtain Stripe API keys', owner: 'DevOps Lead', due_date: '2025-01-15', priority: 'Critical', status: 'In Progress' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Product Track Sheet Draft";
      const payload = {
        name: draftName,
        template_type: "Product Track Sheet",
        template_id: "product-tracksheet",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "product-track-sheet", "Product Track Sheet", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("product-track-sheet", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/product-tracksheet`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Product_Track_Sheet_${formData.project_name.replace(/\s+/g, '_') || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Product Track Sheet",
        formData,
        settings: activeSettings || {},
        templateType: "Product Track Sheet",
        templateId: "product-tracksheet",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addFeature = () => {
    const newId = `FEAT-${String(formData.features.length + 101).padStart(3, '0')}`;
    setFormData({
      ...formData,
      features: [...formData.features, { id: newId, feature_name: '', description: '', priority: 'Medium', status: 'Backlog', assignee: '', estimated_hours: 0, actual_hours: 0, start_date: '', due_date: '', completion_date: '', progress: 0, blockers: '', notes: '' }]
    });
  };

  const updateFeature = (index, field, value) => {
    const features = [...formData.features];
    features[index] = { ...features[index], [field]: value };
    setFormData({ ...formData, features });
  };

  const removeFeature = (index) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  const priorities = ['Critical', 'High', 'Medium', 'Low'];
  const statuses = ['Backlog', 'Ready', 'In Progress', 'In Review', 'Testing', 'Completed', 'On Hold', 'Cancelled'];

  const getStatusColor = (status) => {
    const colors = { 'Completed': 'bg-green-100', 'In Progress': 'bg-blue-100', 'In Review': 'bg-purple-100', 'Testing': 'bg-yellow-100', 'On Hold': 'bg-orange-100', 'Cancelled': 'bg-red-100' };
    return colors[status] || 'bg-gray-50';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Product Track Sheet</h1><p className="text-sm text-gray-500">Product/feature tracking and status</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Track Sheet</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2"><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="E-Commerce Platform v2.0" /></div>
              <div><Label>Product Owner</Label><Input value={formData.product_owner} onChange={(e) => setFormData({...formData, product_owner: e.target.value})} placeholder="Name" /></div>
              <div><Label>Sprint/Iteration</Label><Input value={formData.sprint} onChange={(e) => setFormData({...formData, sprint: e.target.value})} placeholder="Sprint 5" /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Features / Work Items ({formData.features.length})</Label>
              <div className="space-y-3 mt-3">
                {formData.features.map((feat, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${getStatusColor(feat.status)}`}>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                      <div><Label className="text-xs">ID</Label><Input value={feat.id} onChange={(e) => updateFeature(idx, 'id', e.target.value)} placeholder="FEAT-101" className="text-sm font-mono" /></div>
                      <div className="md:col-span-2"><Label className="text-xs">Feature Name *</Label><Input value={feat.feature_name} onChange={(e) => updateFeature(idx, 'feature_name', e.target.value)} placeholder="Feature name" className="text-sm" /></div>
                      <div><Label className="text-xs">Priority</Label>
                        <Select value={feat.priority} onValueChange={(v) => updateFeature(idx, 'priority', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Status</Label>
                        <Select value={feat.status} onValueChange={(v) => updateFeature(idx, 'status', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end"><Button variant="ghost" size="icon" onClick={() => removeFeature(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-2">
                      <div><Label className="text-xs">Assignee</Label><Input value={feat.assignee} onChange={(e) => updateFeature(idx, 'assignee', e.target.value)} placeholder="Name" className="text-sm" /></div>
                      <div><Label className="text-xs">Est. Hours</Label><Input type="number" value={feat.estimated_hours} onChange={(e) => updateFeature(idx, 'estimated_hours', parseInt(e.target.value) || 0)} className="text-sm" /></div>
                      <div><Label className="text-xs">Actual Hours</Label><Input type="number" value={feat.actual_hours} onChange={(e) => updateFeature(idx, 'actual_hours', parseInt(e.target.value) || 0)} className="text-sm" /></div>
                      <div><Label className="text-xs">Due Date</Label><Input type="date" value={feat.due_date} onChange={(e) => updateFeature(idx, 'due_date', e.target.value)} className="text-sm" /></div>
                      <div><Label className="text-xs">Progress %</Label><Input type="number" min="0" max="100" value={feat.progress} onChange={(e) => updateFeature(idx, 'progress', parseInt(e.target.value) || 0)} className="text-sm" /></div>
                      <div><Label className="text-xs">Blockers</Label><Input value={feat.blockers} onChange={(e) => updateFeature(idx, 'blockers', e.target.value)} placeholder="Any blockers" className="text-sm" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addFeature} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Feature</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProductTrackSheetForm;
